import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { ArtifactResponseJSON, RenderRequest, RenderResponse } from "@performyard/service-contracts";

import type { ChatOutput, OutputQuery } from "./types";

import { useReportingChatStore } from "./store";

import { useArtifact, useRenderArtifact } from "../../../../api/hooks";

import { buildArtifactSummary } from "./utils/buildArtifactSummary";
import { TimeoutError, withTimeout } from "./utils/withTimeout";

const RENDER_TIMEOUT_MS = 30_000;
const SUGGESTION_QUERY_MAX_LENGTH = 80;

type ArtifactDetail = ArtifactResponseJSON["artifact"];

const truncate = (text: string, maxLength: number): string =>
	text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

// Rebuilds the frame's nested {reportType, params, output} shape from an artifact's flat stored
// query — the inverse of the flatten SaveArtifactModal does on save — so the refreshed output's
// Save affordance can round-trip through the same flatten logic as a freshly streamed one.
const toNestedQuery = (artifact: ArtifactDetail): OutputQuery | undefined => {
	if (!artifact.query) return undefined;
	const { reportType, ...params } = artifact.query as Record<string, unknown> & {
		reportType: OutputQuery["reportType"];
	};
	return { reportType, params, output: artifact.output };
};

const toChatOutput = (response: RenderResponse): ChatOutput => {
	if (response.outputType === "answer") {
		const suggestions = response.underlyingQuery
			? [`Ask again: ${truncate(response.underlyingQuery, SUGGESTION_QUERY_MAX_LENGTH)}`]
			: undefined;
		return {
			kind: "answer",
			answer: response.answer,
			underlyingQuery: response.underlyingQuery,
			suggestions,
		};
	}
	if (response.outputType === "table") {
		return {
			kind: "table",
			title: response.title,
			rows: response.rows,
			columns: response.columns,
			rowCount: response.rowCount,
			// Carried through so a reopened artifact shows the same truncation notice the chat did —
			// /render re-runs the query under the same display cap.
			truncated: response.truncated,
		};
	}
	return {
		kind: "chart",
		chartType: response.chartType,
		title: response.title,
		labels: response.labels,
		categoryLabel: response.categoryLabel,
		valueLabel: response.valueLabel,
		datasets: response.datasets,
		truncated: response.truncated,
	};
};

// On mount, if the URL carries ?artifactId=, starts a fresh conversation seeded with a summary of
// the saved query (not a chat replay) and re-runs the artifact's query so the output reflects
// current data. Runs once per artifactId.
export const useReopenArtifact = (options?: { enabled?: boolean; abortStream?: () => void }) => {
	const enabled = options?.enabled ?? true;
	const [searchParams] = useSearchParams();
	const artifactId = searchParams.get("artifactId");
	// Keyed by artifactId (not a plain boolean) so reopening a second artifact in a still-mounted
	// chat re-hydrates instead of silently no-oping.
	const hydratedIdRef = useRef<string | null>(null);
	// Local (not store) so a reopen failure never disables ChatInput — Chat/index.tsx renders its
	// own error box off this instead of the shared store error.
	const [renderError, setRenderError] = useState<"timeout" | "failed" | null>(null);
	// Flips true once the withTimeout wrapper settles (success, failure, or timeout) so the
	// "Refreshing…" banner can clear even though the underlying mutation may still be pending.
	const [renderSettled, setRenderSettled] = useState(false);

	const {
		data,
		isLoading: isArtifactQueryLoading,
		isFetching: isArtifactFetching,
		isError: isArtifactError,
	} = useArtifact(artifactId ?? "", {
		enabled: enabled && !!artifactId,
	});
	// isLoading is false on a warm cache even while the revalidation is in flight, and hydration
	// waits for that to settle — so callers gating on "the artifact is loading" have to cover the
	// refetch too, or the chat sits blank and sendable in between.
	const isLoadingArtifact = isArtifactQueryLoading || isArtifactFetching;
	const renderArtifact = useRenderArtifact();

	useEffect(() => {
		// Fires whenever the artifact identity diverges from what's actually hydrated — covers both
		// clearing artifactId (A -> blank) and switching to a different artifact on the same route,
		// where no remount happens. hydratedIdRef is only ever set to a SUCCESSFULLY hydrated id (in
		// the effect below), so this clears the prior artifact's messages/loadedArtifactId/local error
		// state as soon as the identity changes — before the new id's GET resolves — instead of
		// leaving the old artifact live for the whole fetch (and permanently if the fetch fails). The
		// store is also a module singleton, so it otherwise outlives this route's navigation.
		// The store clause covers the remount: hydratedIdRef is per-mount, so entering a blank chat
		// after leaving a reopened one reads null on both sides and would skip the reset, leaving the
		// previous artifact's loadedArtifactId live — and Save silently updates it.
		const { loadedArtifactId } = useReportingChatStore.getState();
		if (artifactId !== hydratedIdRef.current || (artifactId === null && loadedArtifactId !== null)) {
			// Abort the producer before resetting the store — otherwise the old stream's controller
			// stays alive and stays owner, so its isOwner()-gated frames (notably reporting_chat_notes,
			// a top-level store write) keep landing and ride into the next sendQuery's POST body.
			options?.abortStream?.();
			useReportingChatStore.getState().reset();
			hydratedIdRef.current = null;
			setRenderError(null);
			setRenderSettled(false);
		}
	}, [artifactId]);

	useEffect(() => {
		if (!enabled || !artifactId || !data?.artifact) return;
		// A warm react-query cache hands back the previous value with a revalidation still running.
		// Hydrating from it would set hydratedIdRef, and the fresh response — same id, so caught by
		// the guard below — would then be discarded, leaving the session on a stale query whose Save
		// overwrites the newer artifact. Seed only once the data in hand is current.
		if (isArtifactFetching || isArtifactError) return;
		if (hydratedIdRef.current === artifactId) return;
		hydratedIdRef.current = artifactId;
		setRenderError(null);
		setRenderSettled(false);

		const artifact = data.artifact;
		const {
			reset,
			startAssistantMessage,
			appendToAssistantMessage,
			completeAssistantMessage,
			setLoadedArtifactId,
		} = useReportingChatStore.getState();

		reset();
		setLoadedArtifactId(artifactId);

		// Seeded as a normal assistant turn so it both renders as the summary bubble and — via
		// useReportingChatStream's history mapping — becomes the agent's only context for a follow-up.
		const summaryMessageId = startAssistantMessage();
		appendToAssistantMessage(summaryMessageId, buildArtifactSummary(artifact));
		completeAssistantMessage(summaryMessageId);

		const renderRequest: RenderRequest = {
			outputType: artifact.outputType,
			query: artifact.query as RenderRequest["query"],
			output: artifact.output,
		};

		// Guards against a render call that resolves after the timeout already surfaced an error —
		// without it, a late success would attach a second, unexplained output bubble.
		let settled = false;

		withTimeout(renderArtifact.mutateAsync(renderRequest), RENDER_TIMEOUT_MS)
			.then((response) => {
				if (settled) return;
				settled = true;
				setRenderSettled(true);
				// Belt-and-suspenders with the cleanup below: re-checks the singleton directly so this
				// still holds if some future path resets it without that cleanup running.
				if (useReportingChatStore.getState().loadedArtifactId !== artifactId) return;
				try {
					let output = toChatOutput(response);
					if (output.kind !== "answer") {
						output = { ...output, query: toNestedQuery(artifact) };
					}
					useReportingChatStore.getState().attachOutput(summaryMessageId, output);
				} catch (error) {
					console.error("useReopenArtifact: failed to process render response", error);
					setRenderError("failed");
				}
			})
			.catch((error) => {
				if (settled) return;
				settled = true;
				setRenderSettled(true);
				if (useReportingChatStore.getState().loadedArtifactId !== artifactId) return;
				if (error instanceof TimeoutError) {
					setRenderError("timeout");
					return;
				}
				console.error("useReopenArtifact: failed to refresh saved report", error);
				setRenderError("failed");
			});

		// hydratedIdRef guards this from re-running; renderArtifact is intentionally not a dependency.
		return () => {
			settled = true;
		};
	}, [enabled, artifactId, data, isArtifactFetching, isArtifactError]);

	return {
		artifactId,
		isLoadingArtifact,
		isArtifactError,
		isRenderingArtifact: renderArtifact.isPending && !renderSettled,
		renderError,
	};
};

export default useReopenArtifact;
